/**
 * Komal OS - Category Store
 * Manages the category/task tree used for production-tracking configuration.
 */

import { create } from 'zustand';
import * as categoryService from '../services/categoryService';
import * as taskService from '../services/taskService';

const useCategoryStore = create((set, get) => ({
  // State
  categories: [],
  isLoading: false,
  isSubmitting: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.getCategories();
      set({ categories: response.data.categories || [], isLoading: false });
      return response.data.categories;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load categories';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  createCategory: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await categoryService.createCategory(data);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create category';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  updateCategory: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await categoryService.updateCategory(id, data);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update category';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deleteCategory: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await categoryService.deleteCategory(id);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete category';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  createTask: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await taskService.createTask(data);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create task';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  updateTask: async (id, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await taskService.updateTask(id, data);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update task';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deleteTask: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await taskService.deleteTask(id);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete task';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default useCategoryStore;
